/**
 * <luboo-toast> + <luboo-toast-queue> — non-blocking notifications.
 *
 * Two custom elements:
 *
 *   <luboo-toast-queue position="BottomRight">
 *     Singleton positioning host. Lazy-built per position on first toast.
 *     Manages waiting queue + max-visible cap so a connectivity blip
 *     doesn't stack 20 errors on screen.
 *
 *   <luboo-toast variant="success" duration="4000" show>Saved</luboo-toast>
 *     Individual message. Set the `show` attribute → enqueues to its
 *     position's queue. Or use the static API:
 *
 *   LubooToast.success("Saved");
 *   LubooToast.error("Network error");                       // dismiss-required by default
 *   LubooToast.error("Saved offline", { dismissRequired: false });
 *   LubooToast.info("Tip", {
 *     heading: "Heads up",
 *     action: { label: "Undo", onClick: () => undo() },
 *   });
 *
 * Variants map to existing --notice-* tokens in base.css:
 *   info → notice-info, tip → notice-tip, success → notice-success,
 *   warning → notice-attention, error → notice-critical
 *
 * Position: BottomRight on desktop. On viewports ≤ 640px, CSS overrides
 * the queue to span the bottom edge full-width with side margins
 * regardless of the position attribute (touch-target friendly).
 */

const POSITIONS = ['TopLeft', 'TopCenter', 'TopRight', 'BottomLeft', 'BottomCenter', 'BottomRight'];
const POSITION_TO_CLASS = {
  TopLeft: 'top-left',
  TopCenter: 'top-center',
  TopRight: 'top-right',
  BottomLeft: 'bottom-left',
  BottomCenter: 'bottom-center',
  BottomRight: 'bottom-right',
};
const VARIANTS = ['info', 'tip', 'success', 'warning', 'error'];

const VARIANT_DEFAULTS = {
  info: { duration: 4000, dismissRequired: false, role: 'status' },
  tip: { duration: 4000, dismissRequired: false, role: 'status' },
  success: { duration: 4000, dismissRequired: false, role: 'status' },
  warning: { duration: 6000, dismissRequired: false, role: 'status' },
  error: { duration: 8000, dismissRequired: true, role: 'alert' },
};

const VARIANT_ICONS = {
  info: 'luboo:info',
  tip: 'luboo:info',
  success: 'luboo:check-circle',
  warning: 'luboo:alert-triangle',
  error: 'luboo:alert-circle',
};

const DEFAULT_POSITION = 'BottomRight';
const DEFAULT_MAX_VISIBLE = 4;
const DEFAULT_VARIANT = 'info';

/* Toasts route through a single fixed-positioned portal that overlays the
 * full viewport but stays out of body's flex layout. Without the portal,
 * appending queues directly to body caused the workspace-container to
 * shrink: per CSS spec position:fixed should remove the queue from
 * body's flex flow, but with multiple stacked toasts inside, body's
 * flex algorithm was squeezing the workspace anyway. The portal makes
 * isolation unambiguous. */
let _portal = null;
/**
 *
 */
function _ensurePortal() {
  if (_portal && _portal.isConnected) return _portal;
  _portal = document.createElement('div');
  _portal.className = 'luboo-toast-portal';
  document.body.appendChild(_portal);
  return _portal;
}

/* Per-position queue host — lazy-built on first usage and cached. */
const _queueRegistry = new Map();
/**
 *
 * @param position
 */
function _getQueueForPosition(position) {
  if (!POSITIONS.includes(position)) position = DEFAULT_POSITION;
  if (!_queueRegistry.has(position)) {
    const queue = document.createElement('luboo-toast-queue');
    queue.setAttribute('position', position);
    _ensurePortal().appendChild(queue);
    _queueRegistry.set(position, queue);
  }
  return _queueRegistry.get(position);
}

class LubooToastQueue extends HTMLElement {
  static get observedAttributes() {
    return ['position', 'max-visible'];
  }

  constructor() {
    super();
    this._waiting = [];
  }

  connectedCallback() {
    if (!this.hasAttribute('position')) {
      this.setAttribute('position', DEFAULT_POSITION);
    }
    this._applyPosition();
    this.classList.add('luboo-toast-queue');
  }

  attributeChangedCallback(name) {
    if (name === 'position') this._applyPosition();
  }

  get position() {
    return this.getAttribute('position') || DEFAULT_POSITION;
  }

  get maxVisible() {
    const v = parseInt(this.getAttribute('max-visible'), 10);
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_MAX_VISIBLE;
  }

  get _visibleToasts() {
    // Filter by the toast's _state field (set synchronously in
    // _beginDisplay) rather than the .is-displayed CSS class, which
    // arrives 2 rAFs later for transition timing — by which point the
    // dequeue loop has already moved on and the cap fails to apply.
    return Array.from(this.children).filter((n) => n.tagName === 'LUBOO-TOAST' && n._state === 'displayed');
  }

  get _hasSlot() {
    return this._visibleToasts.length < this.maxVisible;
  }

  _applyPosition() {
    for (const cls of Object.values(POSITION_TO_CLASS)) {
      this.classList.remove(cls);
    }
    const cls = POSITION_TO_CLASS[this.position] || POSITION_TO_CLASS[DEFAULT_POSITION];
    this.classList.add(cls);
  }

  enqueue(toast) {
    if (!this._waiting.includes(toast)) this._waiting.push(toast);
    this._dequeue();
  }

  _dequeue() {
    while (this._hasSlot && this._waiting.length > 0) {
      const toast = this._waiting.shift();
      this._display(toast);
    }
  }

  _display(toast) {
    if (toast.parentElement && toast.parentElement !== this) {
      toast.parentElement.removeChild(toast);
    }
    if (toast.parentElement !== this) this.appendChild(toast);
    toast._beginDisplay();
  }

  _onToastDismissed(toast) {
    if (toast.parentElement === this) {
      toast.parentElement.removeChild(toast);
    }
    this._dequeue();
  }
}

class LubooToast extends HTMLElement {
  static get observedAttributes() {
    return ['show'];
  }

  constructor() {
    super();
    this._state = 'idle'; // idle → queued → displayed → dismissed
    this._dismissTimer = null;
    this._built = false;
    this._dismissedResolve = null;
    this._dismissedPromise = new Promise((resolve) => {
      this._dismissedResolve = resolve;
    });
  }

  connectedCallback() {
    if (!this._built) this._build();
    if (this.hasAttribute('show') && this._state === 'idle') {
      this._enqueue();
    }
  }

  disconnectedCallback() {
    this._clearTimer();
  }

  attributeChangedCallback(name) {
    if (name === 'show' && this.hasAttribute('show') && this._state === 'idle' && this._built) {
      this._enqueue();
    }
  }

  get variant() {
    const v = this.getAttribute('variant');
    return VARIANTS.includes(v) ? v : DEFAULT_VARIANT;
  }

  get heading() {
    return this.getAttribute('heading') || '';
  }

  get duration() {
    const d = parseInt(this.getAttribute('duration'), 10);
    if (Number.isFinite(d) && d > 0) return d;
    return VARIANT_DEFAULTS[this.variant].duration;
  }

  get dismissRequired() {
    if (this.hasAttribute('dismiss-required')) {
      return this.getAttribute('dismiss-required') !== 'false';
    }
    return VARIANT_DEFAULTS[this.variant].dismissRequired;
  }

  get position() {
    return this.getAttribute('position') || DEFAULT_POSITION;
  }

  /** Promise that resolves when this toast finishes dismissing. */
  get dismissed() {
    return this._dismissedPromise;
  }

  _build() {
    if (this._built) return;
    this._built = true;
    this.classList.add('luboo-toast', `luboo-toast-${this.variant}`);
    this.setAttribute('role', VARIANT_DEFAULTS[this.variant].role);

    // Capture any inline message content the caller put inside the host
    // (declarative usage: <luboo-toast>Hello</luboo-toast>).
    const messageContent = this.innerHTML.trim();
    this.innerHTML = '';

    const icon = document.createElement('svg-icon');
    icon.setAttribute('icon', VARIANT_ICONS[this.variant]);
    icon.classList.add('toast-icon');
    this.appendChild(icon);

    const body = document.createElement('div');
    body.classList.add('toast-body');
    if (this.heading) {
      const h = document.createElement('div');
      h.classList.add('toast-heading');
      h.textContent = this.heading;
      body.appendChild(h);
    }
    const msg = document.createElement('div');
    msg.classList.add('toast-message');
    msg.innerHTML = messageContent;
    body.appendChild(msg);
    this.appendChild(body);

    if (this._actionConfig) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.classList.add('toast-action');
      btn.textContent = this._actionConfig.label;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        try {
          this._actionConfig.onClick?.();
        } finally {
          this.dismiss();
        }
      });
      this.appendChild(btn);
    }

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.classList.add('toast-close');
    closeBtn.setAttribute('aria-label', 'Dismiss');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.dismiss();
    });
    this.appendChild(closeBtn);

    // Hover pauses auto-dismiss so users can finish reading.
    this.addEventListener('mouseenter', () => {
      this._isHovered = true;
      this._clearTimer();
    });
    this.addEventListener('mouseleave', () => {
      this._isHovered = false;
      if (this._state === 'displayed' && !this.dismissRequired) {
        this._startTimer();
      }
    });
  }

  _enqueue() {
    if (!this._built) this._build();
    this._state = 'queued';
    const queue = _getQueueForPosition(this.position);
    queue.enqueue(this);
  }

  _beginDisplay() {
    this._state = 'displayed';
    // Defer the class flip one frame so the slide-in transition fires from
    // the "off-screen" starting state rather than skipping straight to it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-displayed');
      });
    });
    if (!this.dismissRequired) {
      this._startTimer();
    }
  }

  _startTimer() {
    this._clearTimer();
    this._dismissTimer = window.setTimeout(() => this.dismiss(), this.duration);
  }

  _clearTimer() {
    if (this._dismissTimer) {
      window.clearTimeout(this._dismissTimer);
      this._dismissTimer = null;
    }
  }

  dismiss() {
    if (this._state === 'dismissed') return;
    this._clearTimer();
    if (this._state !== 'displayed') {
      this._finalize();
      return;
    }
    this._state = 'dismissed';
    this.classList.add('is-dismissing');
    this.classList.remove('is-displayed');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.removeEventListener('transitionend', finish);
      this._finalize();
    };
    this.addEventListener('transitionend', finish);
    // Fallback for reduced-motion / edge cases where transitionend never fires.
    window.setTimeout(finish, 400);
  }

  _finalize() {
    this._state = 'dismissed';
    const queue = _queueRegistry.get(this.position);
    if (queue) {
      queue._onToastDismissed(this);
    } else if (this.parentElement) {
      this.parentElement.removeChild(this);
    }
    this.removeAttribute('show');
    if (this._dismissedResolve) {
      this._dismissedResolve();
      this._dismissedResolve = null;
    }
  }

  // ============================================================
  // Static helpers — programmatic API mirroring LubooDialog.alert()
  // ============================================================

  static success(message, opts) {
    return LubooToast._create('success', message, opts);
  }
  static error(message, opts) {
    return LubooToast._create('error', message, opts);
  }
  static warning(message, opts) {
    return LubooToast._create('warning', message, opts);
  }
  static info(message, opts) {
    return LubooToast._create('info', message, opts);
  }
  static tip(message, opts) {
    return LubooToast._create('tip', message, opts);
  }

  /**
   * @param {string} variant
   * @param {string} message
   * @param {object} [opts]
   * @param {string} [opts.heading]
   * @param {number} [opts.duration]
   * @param {string} [opts.position]
   * @param {boolean} [opts.dismissRequired]
   * @param {{ label: string, onClick: () => void }} [opts.action]
   * @returns {{ element: LubooToast, dismiss: () => void, dismissed: Promise<void> }}
   */
  static _create(variant, message, opts = {}) {
    const el = document.createElement('luboo-toast');
    el.setAttribute('variant', variant);
    if (opts.heading) el.setAttribute('heading', opts.heading);
    if (opts.duration != null) el.setAttribute('duration', String(opts.duration));
    if (opts.position) el.setAttribute('position', opts.position);
    if (opts.dismissRequired != null) {
      el.setAttribute('dismiss-required', opts.dismissRequired ? 'true' : 'false');
    }
    if (opts.action) el._actionConfig = opts.action;
    el.textContent = String(message ?? '');
    // Build internal structure without inserting into DOM. _enqueue routes
    // through the queue, which only appendChild's when the toast is ready to
    // display — toasts beyond the visible cap stay fully detached so they
    // never participate in body's flex layout while waiting.
    el._build();
    el._enqueue();
    return {
      element: el,
      dismiss: () => el.dismiss(),
      dismissed: el.dismissed,
    };
  }
}

// Expose globally for inline scripts + console use. Mirrors how
// guest/analytics exposes window.LubooAnalytics. Module callers can still
// use the named import below.
if (typeof window !== 'undefined') {
  window.LubooToast = LubooToast;
}

export { LubooToast, LubooToastQueue };
